import { Loader2, Wrench } from "lucide-react";

import type { LiveToolCall } from "@/hooks/useAnalysisStream";

interface Props {
  liveTokens: string;
  liveToolCalls: LiveToolCall[];
  status: "idle" | "streaming" | "error";
  errorMessage: string | null;
}

/**
 * Render en vivo de la actividad del motor durante un turno (F14).
 *
 * Muestra cada tool call con su estado (ejecutando → completado en Nms),
 * y el texto del modelo creciendo token por token con un cursor
 * parpadeante. Cuando llega `complete`, los componentes superiores
 * invalidan la query y este render se reemplaza por los mensajes
 * persistidos.
 */
export function LiveAnalysisActivity({
  liveTokens,
  liveToolCalls,
  status,
  errorMessage,
}: Props) {
  if (status === "idle" && !liveTokens && liveToolCalls.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {liveToolCalls.map((tc) => (
        <div
          key={tc.tool_use_id || `${tc.tool_name}-${tc.tool_input_summary}`}
          className="flex justify-start text-xs text-muted-foreground animate-in fade-in"
        >
          <div className="max-w-[78%] rounded-md border border-dashed bg-muted/40 px-3 py-2">
            <div className="flex items-center gap-2">
              {tc.state === "running" ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Wrench className="size-3" />
              )}
              <span className="font-mono">{tc.tool_name}</span>
              {tc.state === "running" ? (
                <span className="italic">ejecutando…</span>
              ) : (
                <span className="italic">
                  {tc.ok === false
                    ? "falló"
                    : tc.duration_ms != null
                      ? `completado en ${tc.duration_ms} ms`
                      : "completado"}
                </span>
              )}
            </div>
            {tc.tool_input_summary && (
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted/60 p-1 text-[10px]">
                {tc.tool_input_summary}
              </pre>
            )}
          </div>
        </div>
      ))}

      {(liveTokens || status === "streaming") && (
        <div className="flex justify-start animate-in fade-in">
          <div className="max-w-[78%] rounded-lg border bg-card px-4 py-2 shadow-sm">
            <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
              AROC
            </div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {liveTokens}
              {status === "streaming" && (
                <span className="ml-0.5 inline-block w-[1ch] animate-pulse">
                  ▍
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {status === "error" && errorMessage && (
        <div className="flex justify-center">
          <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-1 text-xs text-destructive">
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
}
